import React, { useEffect, useState } from 'react'
import useAxios from 'hooks/useAxios'
import * as R from 'ramda'

import { Loader } from 'shared/components/Loader'

import type { GroupMapping } from '../DirectorySyncGroupMapping'
import { Button, Label } from 'semantic-ui-react'
import DirectorySyncRemoveSetting from 'components/DirectorySyncRemoveSetting'
import { toast } from 'libs/toast'
import DirectorySyncGroupMapping from '../DirectorySyncGroupMapping'
import { isSyncAvailabled } from 'libs/isSyncAvailabled'
import { getAxios } from 'api'
import useModal from 'hooks/useModal'
import ProFeatureLabel from 'components/ProFeatureLabel'
import usePermission from 'hooks/usePermission'
import constants from 'config/constants'

export default function DirectorySyncContainer({
  isEnabledDirectorySync,
  checkingDirectoryFeatureToggle,
}) {
  const [workOSDirectory, setWorkOSDirectory] = useState<WorkOSDirectory | null>(null)
  const [loadingConfigurationPortal, setLoadingConfigurationPortal] = useState(false)
  const { Modal, open, close } = useModal()
  const { hasPermission } = usePermission()
  const hasManageAuthenSecurityPermission = hasPermission(
    constants.permissions.manageAuthenticationSecurity
  )

  // save settings part
  const { fetch: saveSettings, loading: updating } = useAxios({
    url: 'account/accounts/workos-directory/',
    method: 'put',
    skip: true,
  })

  function handleGroupMappingChange(groupMappings) {
    setWorkOSDirectory(R.assocPath(['directory_group_mappers'], groupMappings, workOSDirectory))
  }

  function handleSyncOptionChange(syncOption) {
    setWorkOSDirectory(R.assocPath(['sync_option'], syncOption, workOSDirectory))
  }

  function updateSettings() {
    saveSettings({
      data: {
        sync_option: workOSDirectory.sync_option,
      },
    })
      .then((response) => {
        setWorkOSDirectory({ ...response.data, state: workOSDirectory?.state })
        toast.success('Settings updated.')
      })
      .catch(() => {
        toast.error('Failed to update settings. Please try again.')
      })
  }

  // sync users part
  const { fetch: triggerSync, loading: triggering } = useAxios({
    url: 'account/accounts/workos-directory/sync-users/',
    method: 'post',
    skip: true,
  })

  function openConfigurationPortal() {
    const url = 'account/accounts/workos-directory/configuration/'
    getAxios()
      .get(url)
      .then((res) => {
        setLoadingConfigurationPortal(false)
        window.open(res.data.link, '_blank', 'noopener,noreferrer')
      })
      .catch(() => {
        setLoadingConfigurationPortal(false)
        toast.error('Oops! Something went wrong. Please try again.')
      })
  }

  function handleSaveAndSync() {
    saveSettings({
      data: {
        directory_group_mappers: workOSDirectory.directory_group_mappers,
        sync_option: workOSDirectory.sync_option,
      },
    })
      .then((response) => {
        setWorkOSDirectory({ ...response.data, state: workOSDirectory?.state })
      })
      .then(() => triggerSync())
      .then(() => {
        close()
        toast.success(
          'Updating and syncing users in progress. This can take a few minutes to complete.'
        )
      })
      .catch(() => {
        toast.error('Failed to update settings or sync users. Please try again.')
      })
  }

  // initial workOSDirectory part
  const { data, loading: loadingWorkOSDirectory } = useAxios({
    url: 'account/accounts/workos-directory/',
    skip: !isEnabledDirectorySync,
  })

  useEffect(() => {
    if (data) {
      setWorkOSDirectory(data)
    }
  }, [data])
  // end of initial workOSDirectory part

  const isActive = Boolean(R.prop('workos_directory_id', workOSDirectory))

  return (
    <DirectorySyncSection
      isActive={isActive}
      isEnabledDirectorySync={isEnabledDirectorySync}
      checkingDirectoryFeatureToggle={checkingDirectoryFeatureToggle}
    >
      {getContent()}{' '}
    </DirectorySyncSection>
  )

  function getContent() {
    if (checkingDirectoryFeatureToggle || loadingWorkOSDirectory) {
      return <Loader withContainer />
    } else {
      if (isEnabledDirectorySync && hasManageAuthenSecurityPermission) {
        if (isActive) {
          return (
            <div className="mt3">
              <Button
                onClick={openConfigurationPortal}
                disabled={loadingConfigurationPortal}
                loading={loadingConfigurationPortal}
                className="mr2"
              >
                Edit directory sync configuration
              </Button>

              <div className="mt4">
                <h3>Group mapping</h3>
                <p>
                  Configure the mapping of user groups to roles within Simplesat. If a user belongs
                  to multiple groups, they will be assigned to the group with the highest level of
                  permissions.
                </p>
                <Button onClick={open}>Edit group mapping</Button>
              </div>

              <DirectorySyncRemoveSetting
                syncOptionValue={workOSDirectory.sync_option}
                onChange={handleSyncOptionChange}
              />

              <Button primary onClick={updateSettings} disabled={updating} loading={updating}>
                Update settings
              </Button>

              <Modal size="tiny">
                <DirectorySyncGroupMapping
                  modalComponent={Modal}
                  onChange={handleGroupMappingChange}
                  groupMappings={workOSDirectory.directory_group_mappers}
                  workosDirectoryType={workOSDirectory.workos_directory_type}
                  handleSaveAndSync={handleSaveAndSync}
                  disabledSaveAndSync={
                    updating ||
                    triggering ||
                    !isSyncAvailabled(workOSDirectory.workos_last_synced_timestamp, 10)
                  }
                  loadingSaveAndSync={updating || triggering}
                  isSyncedGroupDone={workOSDirectory.workos_synced_groups_timestamp !== null}
                />
              </Modal>
            </div>
          )
        } else {
          return (
            <div className="mt3">
              <Button
                primary
                onClick={openConfigurationPortal}
                disabled={loadingConfigurationPortal}
                loading={loadingConfigurationPortal}
              >
                Configure directory sync
              </Button>
            </div>
          )
        }
      } else {
        return null
      }
    }
  }
}

interface WorkOSDirectory {
  id: number
  workos_directory_id: string
  workos_directory_type: string
  workos_last_synced_timestamp: string
  sync_option: string
  directory_group_mappers: GroupMapping[]
  state: string
}

function DirectorySyncSection({
  isActive,
  isEnabledDirectorySync,
  checkingDirectoryFeatureToggle,
  children,
}) {
  return (
    <React.Fragment>
      <h2 className="flex items-center ui header">
        Directory sync
        {isActive && (
          <Label basic className="green">
            Active
          </Label>
        )}
        {!isEnabledDirectorySync && (
          <ProFeatureLabel
            title="Directory sync"
            content="Directory Sync is available on a higher plan. Upgrade to unlock this feature."
            learnMoreLink="https://help.simplesat.io/en/articles/8340321-login-methods-and-single-sign-on#directory-sync"
            shouldShowBillingPageLink={!checkingDirectoryFeatureToggle && !isEnabledDirectorySync}
          />
        )}
      </h2>
      <p>
        Effortlessly synchronize your users between your organization's Identity Provider (IdP) and
        Simplesat.
      </p>
      <a
        target="_blank"
        rel="noreferrer"
        href="https://help.simplesat.io/en/articles/8340321-login-methods-and-single-sign-on#directory-sync"
        className="!mb-4"
      >
        <i className="icon far fa-book" />
        Learn more about directory sync
      </a>
      {children}
    </React.Fragment>
  )
}



// WEBPACK FOOTER //
// ./src/components/DirectorySync/container.tsx